import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma } from '../../servicios/programaService';
import { obtenerConfigCalidad, actualizarAvanceCalidad } from '../../servicios/calidadService';
import '../estilos/Calidad.css';

function FilaObservacion({ act, onCompletar, onEditar }) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [textoEdicion, setTextoEdicion] = useState('');

  const completar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onCompletar(act._id);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const abrirEdicion = () => {
    setMenuAbierto(false);
    setError(null);
    setTextoEdicion(act.observacionCalidad || '');
    setEditando(true);
  };

  const guardarEdicion = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onEditar(act._id, textoEdicion);
      setEditando(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <tr>
      <td style={{ fontWeight: 500, maxWidth: 280 }}>
        {act._rutaGrupos?.length > 0 && (
          <div className="calidad-obs-ruta-grupos">
            {act._rutaGrupos.map((n, i) => (
              <span key={i}>
                {i > 0 && <span className="calidad-obs-ruta-sep">›</span>}
                <span className="calidad-obs-ruta-item">{n}</span>
              </span>
            ))}
          </div>
        )}
        {act.nombre}
      </td>
      <td style={{ whiteSpace: 'pre-wrap', color: '#374151', fontSize: 13 }}>
        {editando ? (
          <div className="calidad-edit-panel">
            <textarea value={textoEdicion} onChange={e => setTextoEdicion(e.target.value)} disabled={guardando} />
            <div className="calidad-edit-panel-actions">
              <button className="calidad-btn-secondary" onClick={() => setEditando(false)} disabled={guardando}>
                Cancelar
              </button>
              <button className="calidad-btn-primary" onClick={guardarEdicion} disabled={guardando || !textoEdicion.trim()}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          act.observacionCalidad
        )}
      </td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="calidad-btn-primary" onClick={completar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Completado'}
          </button>
          <div style={{ position: 'relative' }}>
            <button
              className="calidad-btn-menu"
              onClick={() => setMenuAbierto(v => !v)}
              disabled={guardando}
              title="Más opciones"
            >
              ⋮
            </button>
            {menuAbierto && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setMenuAbierto(false)} />
                <div className="calidad-dropdown">
                  <button className="calidad-dropdown-item" onClick={abrirEdicion}>
                    Editar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {error && <span style={{ color: '#dc2626', fontSize: 11, display: 'block', marginTop: 4 }}>{error}</span>}
      </td>
    </tr>
  );
}

export default function ObservacionesCalidad() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [actividadesCalidad, setActividadesCalidad] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, config] = await Promise.all([obtenerPrograma(), obtenerConfigCalidad()]);
      setDatos(prog);
      setActividadesCalidad(config);
    } catch {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const completarObservacion = useCallback(async (actividadId) => {
    const actualizada = await actualizarAvanceCalidad(datos.programa._id, actividadId, {
      porcentajeAvance: 100,
      observacionCalidadPendiente: false
    });
    setDatos(prev => ({
      ...prev,
      actividades: prev.actividades.map(a => a._id === actividadId ? { ...a, ...actualizada } : a)
    }));
  }, [datos.programa]);

  const editarObservacion = useCallback(async (actividadId, nuevoTexto) => {
    const actualizada = await actualizarAvanceCalidad(datos.programa._id, actividadId, {
      observacionCalidad: nuevoTexto
    });
    setDatos(prev => ({
      ...prev,
      actividades: prev.actividades.map(a => a._id === actividadId ? { ...a, ...actualizada } : a)
    }));
  }, [datos.programa]);

  const actividadesSet = useMemo(() => new Set(actividadesCalidad), [actividadesCalidad]);

  const observaciones = useMemo(() => {
    const mapaSeccion = {};
    for (const s of datos.secciones) {
      if (s.wbsCode) mapaSeccion[s.wbsCode] = s;
    }
    // Mapa wbsCode → nombre para todas las actividades (para construir ruta ancestros)
    const mapaAct = {};
    for (const act of datos.actividades) {
      if (act.wbsCode) mapaAct[act.wbsCode] = act.nombre;
    }

    const getRuta = (wbsCode) => {
      const partes = wbsCode.split('.');
      const ruta = [];
      for (let i = 1; i < partes.length; i++) {
        const code = partes.slice(0, i).join('.');
        const nombre = i === 1
          ? mapaSeccion[code]?.nombre
          : mapaAct[code];
        if (nombre) ruta.push(nombre);
      }
      return ruta;
    };

    return datos.actividades
      .filter(act => act.wbsCode && actividadesSet.has(act.wbsCode) && act.observacionCalidadPendiente)
      .map(act => ({ ...act, _rutaGrupos: getRuta(act.wbsCode).slice(-3) }))
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  }, [datos.actividades, datos.secciones, actividadesSet]);

  if (cargando) {
    return <div className="calidad-loading">Cargando observaciones…</div>;
  }

  if (!datos.programa) {
    return (
      <div className="calidad-container">
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>No hay programa de obra importado</h3>
          <p>Importa un programa en el módulo Programa de Obra para usar esta vista.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calidad-container">
      <div className="calidad-header">
        <h2>Observaciones R0</h2>
      </div>

      {observaciones.length === 0 ? (
        <div className="calidad-empty">
          <div className="calidad-empty-icon">✅</div>
          <h3>Sin observaciones pendientes</h3>
          <p>Las partidas marcadas "Con observaciones" desde Actividades aparecerán aquí.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="calidad-tabla">
            <thead>
              <tr>
                <th>Partida</th>
                <th>Observación</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {observaciones.map(act => (
                <FilaObservacion key={act._id} act={act} onCompletar={completarObservacion} onEditar={editarObservacion} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
